/* ═══════════════════════════════════════════════════════════════
   Wetterdaten laden — Plan und Lauf (Menü „Daten → Wetterdaten laden“)

   Bis November 2026 lief das in `shell.jsx` so:
   - Ein Limit von Open-Meteo kam als 502 an und wurde als „ohne Daten“
     gezählt, samt Hinweis „das ist normal“. Der Lauf ging weiter, und
     jede weitere Region lief gegen dieselbe Grenze.
   - „Nur fehlende auffüllen“ hielt jede Reihe im Kellerbuch für
     vollständig, auch wenn ihr seit dem 6.11. das neue Jahr fehlte.
     Ein Jahrgang, der während der Saison direkt abgerufen wurde (2026
     im August: April bis August), blieb für immer so stehen.
   - Jeder Jahrgang ging einzeln zum Server, auch wenn die Reihe im
     Kellerbuch ihn längst enthielt.
   - Das Ergebnis kam erst am Ende ins Kellerbuch; wer das Fenster
     schloss, verlor alles.

   Jetzt:
   - `wetterPlan` entscheidet, was fehlt (siehe dort).
   - `wetterLauf` holt nacheinander. An der MINUTENGRENZE wartet er
     (65 s, höchstens dreimal je Eintrag), an Stunden- und Tagesgrenze
     hört er auf und nennt die Meldung samt dem, was offen ist. Jede
     geholte Reihe geht sofort ins Kellerbuch.
   - Abgeschlossene Jahrgänge entstehen aus der Reihe (`ausKlimareihe`),
     ohne Aufruf. Zum Server geht nur, was die Reihe nicht hat: die
     laufende Saison und Jahre ohne Reihe.
   Für den ersten Lauf vieler Regionen (≈ 2.000 Abrufe je Region, zwei
   je Stunde) bleibt `wetter-vorladen.js` auf dem Server der richtige
   Weg — es wartet auch Stundengrenzen ab.
   ═══════════════════════════════════════════════════════════════ */
import { NV } from "../kern/konstanten.js";
import { regionZuWein } from "../kern/fachlogik.js";
import { fehlendesJahr, reiheVeraltet, reiheVorlaeufig } from "./jahrgangsvergleich.js";

const TAG_MS = 86400000;
const MINUTE_WARTEN_MS = 65 * 1000;
const MINUTE_VERSUCHE = 3;

/** Einen abgeschlossenen Jahrgang aus der Klimareihe zusammensetzen —
    Gegenstück zu `jahrgangAusReihe` in wetter.js, dasselbe Objekt.
    `null`, wenn das Jahr nicht in der Reihe steht. */
function ausKlimareihe(klima, region, jahr) {
  if (!klima || !klima.monateJeJahr || !Array.isArray(klima.jahre)) return null;
  const j = klima.jahre.find((e) => e.jahr === jahr);
  const monate = klima.monateJeJahr[jahr];
  if (!j || !monate) return null;
  return {
    jahr, sued: !!klima.sued,
    zeitraum: klima.zeitraumJeJahr ? klima.zeitraumJeJahr[jahr] : null,
    frostlabel: klima.frostlabel || null,
    geladen: Date.now(),
    tMittel: j.tMittel, tMax: j.tMax, tMin: j.tMin,
    di: klima.diJeJahr ? klima.diJeJahr[jahr] : null,
    regen: j.regen, sonne: j.sonne, gdd: j.gdd,
    hitzetage: j.hitzetage, frosttage: j.frosttage,
    monate, ausKlimareihe: true,
  };
}

/** Soll ein vorhandener Jahrgangseintrag durch die Reihe ersetzt
    werden? Ja, wenn die Reihe das Jahr hat und der Eintrag fehlt, aus
    einer älteren Fassung stammt (ohne `di`) oder direkt abgerufen
    wurde — ein direkter Abruf eines inzwischen abgeschlossenen Jahres
    endet am Tag des Abrufs, die Reihe nicht. Liefert den neuen
    Eintrag oder `null`. */
function ersatzAusReihe(eintrag, klima, region, jahr) {
  if (eintrag && eintrag.ausKlimareihe && eintrag.di !== undefined) return null;
  return ausKlimareihe(klima, region, jahr);
}

/** Braucht diese Reihe einen Abruf? Grund oder `null`. */
function reiheGrund(klima, jetzt = Date.now()) {
  if (!klima || !Array.isArray(klima.jahre) || klima.jahre.length === 0) return "fehlt";
  if (reiheVeraltet(klima)) return "veraltet";
  if (fehlendesJahr(klima, new Date(jetzt)) != null) return "neuesJahr";
  const letztes = klima.bis != null ? klima.bis : klima.jahre[klima.jahre.length - 1].jahr;
  const v = reiheVorlaeufig(klima, letztes);
  if (v && jetzt >= v.abMs) return "nachlauf";
  return null;
}

/** Regionen mit Koordinaten und ihre Jahrgänge im Bestand. */
function bestand(db) {
  const regionen = (db.regions || []).filter((r) => r.lat != null && r.lon != null);
  const jahre = new Map(regionen.map((r) => [r.id, new Set()]));
  for (const w of db.weine || []) {
    const region = regionZuWein(db, w);
    if (!region || !jahre.has(region.id)) continue;
    for (const jg of w.jahrgaenge || []) {
      if (jg.jahr === NV || !Number.isInteger(jg.jahr)) continue;
      jahre.get(region.id).add(jg.jahr);
    }
  }
  return { regionen, jahre, ohneKoord: (db.regions || []).length - regionen.length };
}

/**
 * Was ist zu tun?
 *
 * `modus = "alle"`: jede Reihe vom Server (dort meist aus dem
 * Speicher, kostet Open-Meteo dann nichts) und jeder Jahrgang neu.
 *
 * `modus = "fehlend"`:
 * - Reihe, wenn sie fehlt, älter als die Rechnung ist, ihr ein
 *   abgeschlossenes Jahr fehlt oder ihr Nachlauf inzwischen im Archiv
 *   liegt (`reiheGrund`).
 * - Jahrgang, wenn er aus der Reihe kommen kann und noch nicht aus ihr
 *   kommt (`ersatzAusReihe`) — ohne Aufruf. Sonst (laufende Saison,
 *   keine Reihe) vom Server, wenn er fehlt oder älter als einen Tag
 *   ist; so lange hält ihn auch der Server.
 * - Wird die Reihe einer Region neu geholt, kommen alle ihre
 *   Jahrgänge danach neu aus ihr.
 *
 * Liefert `{ reihen: [{ region, grund }], jahrgaenge: [{ region, jahr,
 * art }], ohneKoord, regionen, jahrgaengeImBestand }`; `art` ist
 * "ableiten", "server" oder "nachReihe" (entscheidet sich nach dem
 * Abruf der Reihe).
 */
function wetterPlan(db, modus = "fehlend", jetzt = Date.now()) {
  const { regionen, jahre, ohneKoord } = bestand(db);
  const klimaCache = db.klimaCache || {};
  const weatherCache = db.weatherCache || {};
  const reihen = [];
  const jahrgaenge = [];
  let jahrgaengeImBestand = 0;
  for (const region of regionen) {
    const klima = klimaCache[region.id] || null;
    const grund = modus === "alle" ? (reiheGrund(klima, jetzt) || "alle") : reiheGrund(klima, jetzt);
    if (grund) reihen.push({ region, grund });
    for (const jahr of [...jahre.get(region.id)].sort((a, b) => a - b)) {
      jahrgaengeImBestand++;
      if (grund) { jahrgaenge.push({ region, jahr, art: "nachReihe" }); continue; }
      const eintrag = weatherCache[`${region.id}:${jahr}`] || null;
      if (ausKlimareihe(klima, region, jahr)) {
        if (modus === "alle" || ersatzAusReihe(eintrag, klima, region, jahr)) jahrgaenge.push({ region, jahr, art: "ableiten" });
        continue;
      }
      const alt = !eintrag || !eintrag.geladen || jetzt - eintrag.geladen > TAG_MS;
      if (modus === "alle" || alt) jahrgaenge.push({ region, jahr, art: "server" });
    }
  }
  return { reihen, jahrgaenge, ohneKoord, regionen: regionen.length, jahrgaengeImBestand };
}

/** Wie viele Aufrufe gehen zum Server? „nachReihe“ zählt als Server,
    solange nicht feststeht, ob die neue Reihe das Jahr hat — für die
    Ankündigung eine obere Schranke. */
function planZahlen(plan) {
  const ableiten = plan.jahrgaenge.filter((j) => j.art === "ableiten").length;
  return {
    reihen: plan.reihen.length,
    ableiten,
    server: plan.jahrgaenge.length - ableiten,
    gesamt: plan.reihen.length + plan.jahrgaenge.length,
  };
}

/** Ein Teilergebnis ins Kellerbuch übernehmen (für `setDb(prev => …)`).
    `klima`: neue Reihen je Region — die aus der alten Reihe
    abgeleiteten Jahrgänge dieser Region werden verworfen (wie
    „Klimareihe aktualisieren“ im Witterungspanel); direkt abgerufene
    bleiben. `wetter`: Jahrgangseinträge je Schlüssel. */
function wendeAn(prev, { klima = {}, wetter = {} }) {
  const ids = Object.keys(klima);
  if (ids.length === 0 && Object.keys(wetter).length === 0) return prev;
  const weatherCache = {};
  for (const [k, v] of Object.entries(prev.weatherCache || {})) {
    if (v && v.ausKlimareihe && ids.some((id) => k.startsWith(`${id}:`))) continue;
    weatherCache[k] = v;
  }
  return {
    ...prev,
    klimaCache: { ...(prev.klimaCache || {}), ...klima },
    weatherCache: { ...weatherCache, ...wetter },
  };
}

const ABBRUCH = Symbol("abbruch");
const keineDaten = (e) => /Vegetationsdaten|Zukunft/.test((e && e.message) || "");

/**
 * Den Plan abarbeiten. Alles von außen, damit es ohne Netz und Uhr
 * prüfbar ist:
 *   holeKlima(region), holeJahrgang(region, jahr)  — Serveraufrufe
 *   uebernehme(teil)   — Teilergebnis ins Kellerbuch (siehe `wendeAn`)
 *   melde(stand)       — Fortschritt für die Oberfläche
 *   warte(ms), jetzt() — für die Minutengrenze
 * `klimaCache` ist der Stand vor dem Lauf (für „ableiten“).
 * Liefert den Endstand: `{ phase: "fertig" | "abgebrochen", gesamt,
 * erledigt, reihen, server, abgeleitet, ohneDaten, fehler,
 * letzterFehler, abbruch: { grenze, text, offen } | null }`.
 */
async function wetterLauf(plan, { klimaCache = {}, holeKlima, holeJahrgang, uebernehme, melde = () => {},
  warte = (ms) => new Promise((r) => setTimeout(r, ms)), jetzt = () => Date.now() }) {
  const stand = {
    phase: "laeuft", gesamt: plan.reihen.length + plan.jahrgaenge.length, erledigt: 0,
    reihen: 0, server: 0, abgeleitet: 0, ohneDaten: 0, fehler: 0,
    letzterFehler: null, wartetBis: null, abbruch: null,
  };
  const zeige = () => melde({ ...stand });
  zeige();

  /* Einen Aufruf mit Warten an der Minutengrenze. */
  const mitWarten = async (fn) => {
    for (let versuch = 0; ; versuch++) {
      try {
        return await fn();
      } catch (e) {
        if (e && e.status === 429 && e.grenze === "minute" && versuch < MINUTE_VERSUCHE) {
          stand.wartetBis = jetzt() + MINUTE_WARTEN_MS; zeige();
          await warte(MINUTE_WARTEN_MS);
          stand.wartetBis = null; zeige();
          continue;
        }
        if (e && e.status === 429) {
          stand.abbruch = { grenze: e.grenze || null, text: e.message };
          return ABBRUCH;
        }
        throw e;
      }
    }
  };

  const neueReihen = {};
  for (const { region } of plan.reihen) {
    try {
      const r = await mitWarten(() => holeKlima(region));
      if (r === ABBRUCH) break;
      neueReihen[region.id] = r;
      uebernehme({ klima: { [region.id]: r } });
      stand.reihen++;
    } catch (e) {
      stand.fehler++; stand.letzterFehler = (e && e.message) || String(e);
    }
    stand.erledigt++; zeige();
  }

  /* Jahrgänge. Nach einem Abbruch geht nichts mehr zum Server, aber
     was aus einer Reihe kommen kann, entsteht trotzdem — ohne Netz. */
  const wetter = {};
  for (const { region, jahr, art } of plan.jahrgaenge) {
    const klima = neueReihen[region.id] || klimaCache[region.id] || null;
    const schluessel = `${region.id}:${jahr}`;
    const abgeleitet = art !== "server" ? ausKlimareihe(klima, region, jahr) : null;
    if (abgeleitet) {
      wetter[schluessel] = abgeleitet; stand.abgeleitet++;
    } else {
      if (stand.abbruch) continue;
      try {
        const w = await mitWarten(() => holeJahrgang(region, jahr));
        if (w === ABBRUCH) continue;
        wetter[schluessel] = w; stand.server++;
      } catch (e) {
        if (keineDaten(e)) stand.ohneDaten++;
        else { stand.fehler++; stand.letzterFehler = (e && e.message) || String(e); }
      }
    }
    stand.erledigt++;
    if (stand.erledigt % 10 === 0) zeige();
  }
  uebernehme({ wetter });

  if (stand.abbruch) stand.abbruch.offen = stand.gesamt - stand.erledigt;
  stand.phase = stand.abbruch ? "abgebrochen" : "fertig";
  stand.wartetBis = null;
  zeige();
  return { ...stand };
}

export { ausKlimareihe, ersatzAusReihe, planZahlen, reiheGrund, wendeAn, wetterLauf, wetterPlan, MINUTE_WARTEN_MS };
